import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useUserRole } from "../../hooks/useUserRole";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BarChart3,
  Users,
  CreditCard,
  DollarSign,
  LineChart as LineChartIcon,
  Star,
  Download,
  FileJson,
  Loader2,
  RefreshCw,
  AlertTriangle,
  UserCheck,
  Cpu,
} from "lucide-react";
import toast from "react-hot-toast";
import { adminService } from "../../apis/adminService";
import PaymentDateRangeModal, {
  DateRangeFilterButton,
  formatDateRangeLabel,
} from "./PaymentDateRangeModal";
import SupportFeedbackAdmin from "./SupportFeedbackAdmin";

const LIVE_REFRESH_MS = 30000;

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "traffic", label: "Traffic" },
  { id: "users", label: "Users" },
  { id: "revenue", label: "Revenue" },
  { id: "subs", label: "Subscriptions" },
  { id: "transactions", label: "Transactions" },
  { id: "feedback", label: "Feedback" },
];

const TAB_THEMES = {
  active:
    "bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-sm",
  inactive:
    "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
};

const FILTER_BTN_ACTIVE =
  "bg-gradient-to-r from-blue-600 to-teal-600 text-white shadow-sm";
const FILTER_BTN_INACTIVE =
  "bg-white text-slate-600 hover:bg-gray-100 hover:text-gray-900";

const TAB_KPI_KEYS = {
  overview: ["traffic", "trialUsers", "paidUsers", "totalUsers", "revenue", "chartsMade", "avgRating"],
  traffic: ["traffic", "chartsMade", "tokenUsage"],
  users: ["trialUsers", "paidUsers", "totalUsers"],
  revenue: ["revenue", "paidUsers"],
  subs: ["paidUsers"],
  feedback: ["avgRating", "positiveRate"],
  transactions: ["revenue", "paidUsers"],
};

const KPI_CONFIG = {
  traffic: { icon: BarChart3, label: "Active users", format: "number" },
  trialUsers: { icon: Users, label: "Trial users", format: "number" },
  paidUsers: { icon: CreditCard, label: "Paid users", format: "number" },
  totalUsers: { icon: UserCheck, label: "Total users", format: "number" },
  revenue: { icon: DollarSign, label: "Revenue", format: "currency" },
  chartsMade: { icon: LineChartIcon, label: "SOAP notes", format: "number" },
  tokenUsage: { icon: Cpu, label: "AI tokens", format: "number" },
  avgRating: { icon: Star, label: "Avg rating", format: "rating" },
  totalRatings: { icon: Star, label: "Total ratings", format: "number" },
  categoriesTracked: { icon: BarChart3, label: "Categories tracked", format: "number" },
  positiveRate: { icon: Star, label: "Positive rate", format: "percentage" },
};

const DATE_PRESETS = [
  { id: "7d", label: "7 days" },
  { id: "month", label: "This month" },
  { id: "30d", label: "Last 30 days" },
];

const TIME_X_AXIS = {
  dataKey: "label",
  tick: { fontSize: 12 },
  interval: 0,
  angle: -45,
  textAnchor: "end",
  height: 56,
  minTickGap: 0,
};

const TIME_CHART_MARGIN = { top: 8, right: 16, left: 4, bottom: 8 };

const PLAN_OPTIONS = [
  { value: "all", label: "All plans" },
  { value: "free", label: "Free" },
  { value: "premium", label: "Premium" },
  { value: "starter", label: "Starter" },
  { value: "professional", label: "Professional" },
  { value: "elite", label: "Elite" },
  { value: "individual", label: "Individual (legacy)" },
  { value: "group", label: "Group (legacy)" },
];

const REGION_OPTIONS = [
  { value: "all", label: "All regions" },
  { value: "na", label: "North America" },
  { value: "eu", label: "Europe" },
  { value: "apac", label: "Asia Pacific" },
  { value: "latam", label: "Latin America" },
];

const COLORS = {
  primary: "#6366F1",
  teal: "#1D9E75",
  amber: "#EF9F27",
  red: "#E24B4A",
  blue: "#378ADD",
  purple: "#7F77DD",
  coral: "#D85A30",
};

function fmt(value) {
  const v = Number(value) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return Math.round(v).toLocaleString();
}

function fmtCurrency(value) {
  const v = Number(value) || 0;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function ChangeBadge({ change }) {
  const value = Number(change) || 0;
  const positive = value >= 0;
  return (
    <span
      className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${positive
        ? "bg-green-100 text-green-700"
        : "bg-red-100 text-red-700"
        }`}
    >
      {positive ? "▲" : "▼"} {Math.abs(value)}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, change, format = "number" }) {
  const display =
    format === "currency"
      ? fmtCurrency(value)
      : format === "rating"
        ? `${Number(value || 0).toFixed(1)} / 5`
        : format === "percentage"
          ? `${value !== undefined && value !== null ? value : 0}%`
          : fmt(value);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-gray-500 mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-xl font-semibold text-gray-900 tabular-nums">{display}</div>
      <div className="flex items-center gap-2 mt-1.5">
        <ChangeBadge change={change} />
        <span className="text-xs text-gray-400">vs last</span>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = "" }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-4 shadow-sm ${className}`}>
      <div className="font-medium text-sm text-gray-900">{title}</div>
      {subtitle && <div className="text-xs text-gray-500 mb-3">{subtitle}</div>}
      {!subtitle && <div className="mb-3" />}
      {children}
    </div>
  );
}

function buildChartRows(labels = [], series = {}, planRevenueSeries = {}) {
  return labels.map((label, i) => ({
    label,
    traffic: series.traffic?.[i] || 0,
    trialUsers: series.trialUsers?.[i] || 0,
    returnUsers: series.returnUsers?.[i] || 0,
    paidUsers: series.paidUsers?.[i] || 0,
    chartsCreated: series.chartsCreated?.[i] || 0,
    tokenInput: series.tokenInput?.[i] || 0,
    tokenOutput: series.tokenOutput?.[i] || 0,
    tokenCached: series.tokenCached?.[i] || 0,
    revenue: series.revenue?.[i] || 0,
    mrr: series.mrr?.[i] || 0,
    transactions: series.transactions?.[i] || 0,
    feedbackScore: series.feedbackScore?.[i] || 0,
    churned: series.churned?.[i] || 0,
    renewed: series.renewed?.[i] || 0,
    individualMonthly: planRevenueSeries.individual_monthly?.[i] || 0,
    individualAnnually: planRevenueSeries.individual_annually?.[i] || 0,
    groupMonthly: planRevenueSeries.group_monthly?.[i] || 0,
    groupAnnually: planRevenueSeries.group_annually?.[i] || 0,
  }));
}

function getExportSlug(meta) {
  if (meta?.fromDate && meta?.toDate) {
    return `${meta.fromDate}_to_${meta.toDate}`;
  }
  return meta?.preset || meta?.timeframe || "dashboard";
}

export default function GlobalDashboard() {
  const { isAdmin } = useUserRole();
  const [activeTab, setActiveTab] = useState("overview");
  const [datePreset, setDatePreset] = useState("30d");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [plan, setPlan] = useState("all");
  const [region, setRegion] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [txFilter, setTxFilter] = useState("all");
  const [searchVal, setSearchVal] = useState("");
  const [dailyUserSearch, setDailyUserSearch] = useState("");

  const queryParams = useMemo(() => {
    const params = { plan, region };
    if (fromDate && toDate) {
      params.fromDate = fromDate;
      params.toDate = toDate;
    } else {
      params.timeframe = datePreset;
    }
    return params;
  }, [datePreset, fromDate, toDate, plan, region]);

  const fetchDashboard = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const result = await adminService.getGlobalDashboard(queryParams);
        if (result.success) {
          setData(result.data);
          setLastUpdated(new Date());
        } else if (!silent) {
          toast.error(result.message || "Failed to load dashboard");
        }
      } catch (error) {
        console.error(error);
        if (!silent) {
          toast.error("Failed to load dashboard");
        }
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [queryParams]
  );

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboard({ silent: true });
    }, LIVE_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  useEffect(() => {
    setSearchVal("");
  }, [activeTab]);

  const chartRows = useMemo(
    () => buildChartRows(data?.labels, data?.series, data?.planRevenueSeries),
    [data]
  );

  const filteredTransactions = useMemo(() => {
    const rows = data?.transactions || [];
    const q = searchVal.trim().toLowerCase();
    return rows.filter((row) => {
      if (txFilter !== "all" && row.status !== txFilter) return false;
      if (
        q &&
        !String(row.user || "").toLowerCase().includes(q) &&
        !String(row.id || "").toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [data, txFilter, searchVal
  ]);

  const dailyActiveUserRows = useMemo(() => {
    const rows = data?.dailyActiveUserActivity || [];
    const q = dailyUserSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        String(row.email || "").toLowerCase().includes(q) ||
        (row.day && new Date(row.day).toLocaleDateString().toLowerCase().includes(q))
    );
  }, [data, dailyUserSearch]);

  const dailyActiveUserChartData = useMemo(() => {
    return dailyActiveUserRows
      .filter((row) => row.soapNoteCount > 0)
      .slice(0, 20)
      .map((row) => {
        const dayLabel = row.day ? new Date(row.day).toLocaleDateString() : "—";
        const email = row.email || "Unknown";
        const shortEmail = email.length > 22 ? `${email.slice(0, 20)}…` : email;
        return {
          ...row,
          dayLabel,
          chartLabel: `${shortEmail} (${dayLabel})`,
        };
      });
  }, [dailyActiveUserRows]);

  const exportCsv = () => {
    if (!data?.series) return;
    const meta = data.meta || {};
    const rangeLabel = formatDateRangeLabel(meta.fromDate, meta.toDate);
    const metaRows = [
      `# vbot global dashboard export`,
      `# date_range,${rangeLabel}`,
      `# from,${meta.fromDate || ""}`,
      `# to,${meta.toDate || ""}`,
      `# plan_filter,${meta.plan || plan}`,
      `# region_filter,${meta.region || region}`,
      `# generated_at,${meta.generatedAt || new Date().toISOString()}`,
      "",
    ];
    const headers = [
      "period",
      "activeUsers",
      "trialUsers",
      "returnUsers",
      "paidUsers",
      "chartsCreated",
      "revenue",
      "mrr",
      "transactions",
    ];
    const rows = chartRows.map((row) =>
      [
        row.label,
        row.traffic,
        row.trialUsers,
        row.returnUsers,
        row.paidUsers,
        row.chartsCreated,
        row.revenue,
        row.mrr,
        row.transactions,
      ].join(",")
    );

    const revenuePlanHeaders = ["plan", "billing_period", "revenue", "transactions"];
    const revenuePlanRows = (data.revenueByPlan || []).map((row) =>
      [
        row.planKey || "",
        row.billingPeriod || "",
        row.revenue,
        row.transactions,
      ].join(",")
    );

    const blob = new Blob(
      [
        ...metaRows,
        headers.join(","),
        ...rows,
        "",
        "# revenue_by_plan",
        revenuePlanHeaders.join(","),
        ...revenuePlanRows,
      ].join("\n"),
      { type: "text/csv" }
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vbot-analytics-${getExportSlug(meta)}.csv`;
    a.click();
  };

  const exportJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vbot-analytics-${getExportSlug(data.meta)}.json`;
    a.click();
  };

  const applyDatePreset = (presetId) => {
    setDatePreset(presetId);
    setFromDate("");
    setToDate("");
  };

  const applyCustomDateRange = ({ fromDate: nextFrom, toDate: nextTo }) => {
    setDatePreset("custom");
    setFromDate(nextFrom || "");
    setToDate(nextTo || "");
  };

  const activeRangeLabel = formatDateRangeLabel(
    fromDate || data?.meta?.fromDate,
    toDate || data?.meta?.toDate
  );

  const statusBadge = (status) => {
    const map = {
      completed: "bg-green-100 text-green-700",
      pending: "bg-amber-100 text-amber-700",
      failed: "bg-red-100 text-red-700",
      processing: "bg-blue-100 text-blue-700",
      refunded: "bg-purple-100 text-purple-700",
      cancelled: "bg-gray-100 text-gray-700",
    };
    return map[status] || "bg-gray-100 text-gray-700";
  };

  if (!isAdmin) {
    return <Navigate to="/scribe" replace />;
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[320px] text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading analytics dashboard...
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const visibleKpiKeys = TAB_KPI_KEYS[activeTab] || TAB_KPI_KEYS.overview;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500">
            Active users, trial & paid accounts, SOAP notes, revenue, and misuse monitoring
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white/80 p-1 overflow-hidden shadow-sm">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyDatePreset(preset.id)}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${datePreset === preset.id && !fromDate && !toDate
                  ? FILTER_BTN_ACTIVE
                  : FILTER_BTN_INACTIVE
                  }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <DateRangeFilterButton
            fromDate={fromDate || data?.meta?.fromDate}
            toDate={toDate || data?.meta?.toDate}
            onClick={() => setDateModalOpen(true)}
            className="text-xs py-1.5"
          />
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
          >
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
          >
            {REGION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </button>
          <button
            type="button"
            onClick={exportJson}
            className="inline-flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50"
          >
            <FileJson className="w-3.5 h-3.5" />
            JSON
          </button>
          <button
            type="button"
            onClick={() => fetchDashboard({ silent: true })}
            className="inline-flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <div className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <span className={`w-2 h-2 rounded-full bg-green-500 ${refreshing ? "animate-pulse" : ""}`} />
            Live
            {lastUpdated && (
              <span className="text-xs text-gray-400">
                · {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-600">
        Showing <span className="font-medium text-gray-900">{activeRangeLabel}</span>
      </p>

      <PaymentDateRangeModal
        open={dateModalOpen}
        onOpenChange={setDateModalOpen}
        fromDate={fromDate || data?.meta?.fromDate}
        toDate={toDate || data?.meta?.toDate}
        onApply={applyCustomDateRange}
      />

      <div className="inline-flex gap-1.5 overflow-x-auto p-1 rounded-xl border border-slate-200 bg-white/80 shadow-sm">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-xs rounded-lg whitespace-nowrap font-medium transition-all duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${isActive ? TAB_THEMES.active : TAB_THEMES.inactive
                }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        className={`grid gap-2 sm:gap-3 ${visibleKpiKeys.length >= 7
          ? "grid-cols-2 md:grid-cols-4 xl:grid-cols-7"
          : visibleKpiKeys.length >= 6
            ? "grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
            : visibleKpiKeys.length >= 4
              ? "grid-cols-2 md:grid-cols-4"
              : visibleKpiKeys.length >= 2
                ? "grid-cols-2"
                : "grid-cols-1 max-w-xs"
          }`}
      >
        {visibleKpiKeys.map((key) => {
          const config = KPI_CONFIG[key];
          if (!config) return null;
          return (
            <KpiCard
              key={key}
              icon={config.icon}
              label={config.label}
              value={kpis[key]?.value}
              change={kpis[key]?.change}
              format={config.format}
            />
          );
        })}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <ChartCard title="Revenue & MRR" subtitle="Subscription and transaction revenue trend" className="xl:col-span-2">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmtCurrency(v)} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Area type="monotone" dataKey="revenue" stroke={COLORS.teal} fill={`${COLORS.teal}33`} name="Revenue" />
                  <Area type="monotone" dataKey="mrr" stroke={COLORS.primary} fill={`${COLORS.primary}22`} name="MRR" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Plan breakdown" subtitle="Active users by tier">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data?.planBreakdown || []} dataKey="value" nameKey="name" innerRadius={50} outerRadius={70}>
                    {(data?.planBreakdown || []).map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {(data?.planBreakdown || []).map((p) => (
                  <div key={p.key} className="flex justify-between text-xs text-gray-600">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
                      {p.name}
                    </span>
                    <span className="font-medium text-gray-900">{fmt(p.value)}</span>
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="User growth" subtitle="Trial and returning users">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="trialUsers" fill={COLORS.teal} name="Trial" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="returnUsers" fill={COLORS.primary} name="Returning" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Active users & SOAP notes" subtitle="Daily logins and notes generated">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="traffic" stroke={COLORS.purple} dot={false} name="Active users" />
                  <Line type="monotone" dataKey="chartsCreated" stroke={COLORS.blue} dot={false} strokeDasharray="4 4" name="SOAP notes" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <ChartCard title="Revenue by region">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="pb-2">Region</th>
                    <th className="pb-2">Users</th>
                    <th className="pb-2">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.regions || []).map((row) => (
                    <tr key={row.key} className="border-b border-gray-100">
                      <td className="py-2 font-medium">{row.region}</td>
                      <td className="py-2 text-gray-500">{fmt(row.users)}</td>
                      <td className="py-2 text-green-600 font-medium">{fmtCurrency(row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      )}

      {activeTab === "traffic" && (
        <div className="space-y-3">
          <ChartCard
            title="Daily active users"
            subtitle="Users who created SOAP notes or logged in, with per-day note counts"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <input
                value={dailyUserSearch}
                onChange={(e) => setDailyUserSearch(e.target.value)}
                placeholder="Search by email or date…"
                className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 w-52"
              />
              <span className="text-xs text-gray-500">
                {dailyActiveUserRows.length} active user{dailyActiveUserRows.length === 1 ? "" : "s"}
              </span>
            </div>
            {dailyActiveUserChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(220, dailyActiveUserChartData.length * 28)}>
                <BarChart
                  layout="vertical"
                  data={dailyActiveUserChartData}
                  margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="chartLabel"
                    tick={{ fontSize: 12 }}
                    width={160}
                  />
                  <Tooltip
                    formatter={(value) => [fmt(value), "SOAP notes"]}
                    labelFormatter={(_, payload) => {
                      const row = payload?.[0]?.payload;
                      if (!row) return "";
                      return `${row.email} · ${row.dayLabel}`;
                    }}
                  />
                  <Bar dataKey="soapNoteCount" fill={COLORS.coral} name="SOAP notes" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-10 text-center text-sm text-gray-500">
                No SOAP notes created by active users in this period
              </div>
            )}
            <div className="overflow-x-auto mt-4 border-t border-gray-100 pt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="pb-2">Date</th>
                    <th className="pb-2">Email</th>
                    <th className="pb-2">SOAP notes created</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyActiveUserRows.length ? (
                    dailyActiveUserRows.map((row) => (
                      <tr key={`${row.userId}-${row.day}`} className="border-b border-gray-100">
                        <td className="py-2 text-gray-500 text-xs whitespace-nowrap">
                          {row.day ? new Date(row.day).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-2 font-medium text-xs">{row.email}</td>
                        <td className="py-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium tabular-nums">
                            {fmt(row.soapNoteCount)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="py-6 text-center text-gray-500">
                        No active users match your search
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </ChartCard>
          <ChartCard title="Active users trend" subtitle="Unique logins per day">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartRows} margin={TIME_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis {...TIME_X_AXIS} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area type="monotone" dataKey="traffic" stroke={COLORS.purple} fill={`${COLORS.purple}33`} name="Active users" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="SOAP notes created" subtitle="Clinical notes generated over time">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="chartsCreated" fill={COLORS.coral} name="SOAP notes" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Transaction volume" subtitle="Payments over time">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="transactions" stroke={COLORS.amber} fill={`${COLORS.amber}22`} name="Transactions" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <ChartCard
              title="AI token usage"
              subtitle="Input, output, and cached tokens over time"
            >
              <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-600">
                <span>Input: <strong className="text-indigo-600">{fmt(data?.tokenUsage?.totals?.input)}</strong></span>
                <span>Output: <strong className="text-blue-600">{fmt(data?.tokenUsage?.totals?.output)}</strong></span>
                <span>Cached: <strong className="text-teal-600">{fmt(data?.tokenUsage?.totals?.cached)}</strong></span>
                <span>Total: <strong className="text-gray-900">{fmt(data?.tokenUsage?.totals?.total)}</strong></span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmt(v)} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Bar dataKey="tokenInput" stackId="tokens" fill={COLORS.primary} name="Input" />
                  <Bar dataKey="tokenOutput" stackId="tokens" fill={COLORS.blue} name="Output" />
                  <Bar dataKey="tokenCached" stackId="tokens" fill={COLORS.teal} name="Cached" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard
              title="Token usage by operation"
              subtitle="SOAP note, magic editor, HPI depth, pronouns, age/gender, etc."
            >
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={(data?.tokenUsage?.byOperation || []).map((row) => ({
                    name: row.name,
                    input: row.input,
                    output: row.output,
                    cached: row.cached,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmt(v)} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Bar dataKey="input" stackId="op" fill={COLORS.primary} name="Input" />
                  <Bar dataKey="output" stackId="op" fill={COLORS.blue} name="Output" />
                  <Bar dataKey="cached" stackId="op" fill={COLORS.teal} name="Cached" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="overflow-x-auto mt-3 border-t border-gray-100 pt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                      <th className="pb-2">Operation</th>
                      <th className="pb-2">Calls</th>
                      <th className="pb-2">Input</th>
                      <th className="pb-2">Output</th>
                      <th className="pb-2">Cached</th>
                      <th className="pb-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.tokenUsage?.byOperation || []).length ? (
                      data.tokenUsage.byOperation.map((row) => (
                        <tr key={row.key} className="border-b border-gray-100">
                          <td className="py-2 font-medium text-xs">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-sm" style={{ background: row.color }} />
                              {row.name}
                            </span>
                          </td>
                          <td className="py-2 text-gray-500">{fmt(row.calls)}</td>
                          <td className="py-2 text-indigo-600">{fmt(row.input)}</td>
                          <td className="py-2 text-blue-600">{fmt(row.output)}</td>
                          <td className="py-2 text-teal-600">{fmt(row.cached)}</td>
                          <td className="py-2 font-medium">{fmt(row.total)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-gray-500">
                          No token usage recorded in this period yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>
          <ChartCard title="Misuse flags" subtitle={`Accounts generating ${data?.misuseThreshold || 50}+ SOAP notes in a single day`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Unusual chart volume detected
              </span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${(data?.misuseFlags || []).length
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
                }`}>
                {(data?.misuseFlags || []).length} flagged
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="pb-2">Account</th>
                    <th className="pb-2">Email</th>
                    <th className="pb-2">Date</th>
                    <th className="pb-2">SOAP notes</th>
                    <th className="pb-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.misuseFlags || []).length ? (
                    data.misuseFlags.map((row) => (
                      <tr key={`${row.userId}-${row.day}`} className="border-b border-gray-100">
                        <td className="py-2 font-medium">{row.account}</td>
                        <td className="py-2 text-gray-500 text-xs">{row.email}</td>
                        <td className="py-2 text-gray-500 text-xs">
                          {row.day ? new Date(row.day).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                            {fmt(row.chartCount)}
                          </span>
                        </td>
                        <td className="py-2 text-gray-500 text-xs">{row.reason}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-gray-500">
                        No misuse flags in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      )}

      {activeTab === "users" && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Trial users</div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">
                {fmt(kpis.trialUsers?.value)}
              </div>
              <div className="text-xs text-gray-500 mt-1">New signups in period</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase tracking-wide text-gray-500">Paid users</div>
                <span className="text-xs font-semibold text-amber-600 tabular-nums">
                  {fmt(data?.userCategories?.paidUsers ?? kpis.paidUsers?.value)}
                </span>
              </div>
              <div className="text-2xl font-semibold text-amber-600 tabular-nums">
                {fmt(data?.userCategories?.paidUsers ?? kpis.paidUsers?.value)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Active premium subscribers</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Total users</div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">
                {fmt(data?.userCategories?.totalUsers ?? kpis.totalUsers?.value)}
              </div>
              <div className="text-xs text-gray-500 mt-1">All registered accounts</div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="User segments" subtitle="Trial, returning, and paid users">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="trialUsers" stackId="a" fill={COLORS.teal} name="Trial" />
                  <Bar dataKey="returnUsers" stackId="a" fill={COLORS.primary} name="Returning" />
                  <Bar dataKey="paidUsers" stackId="a" fill={COLORS.amber} name="Paid" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Renewals vs churn" subtitle="Subscription activity proxy">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="renewed" stroke={COLORS.teal} dot={false} name="Renewed" />
                  <Line type="monotone" dataKey="churned" stroke={COLORS.red} dot={false} strokeDasharray="4 4" name="Churned" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <ChartCard title="Return user cohorts">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="pb-2">Period</th>
                    <th className="pb-2">Trial users</th>
                    <th className="pb-2">Returned</th>
                    <th className="pb-2">Paid</th>
                    <th className="pb-2">Return rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.cohorts || []).map((row) => (
                    <tr key={row.period} className="border-b border-gray-100">
                      <td className="py-2">{row.period}</td>
                      <td className="py-2 text-gray-500">{fmt(row.trialUsers)}</td>
                      <td className="py-2 text-gray-500">{fmt(row.returned)}</td>
                      <td className="py-2 text-amber-600 font-medium">{fmt(row.paid)}</td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${row.returnRate > 55
                          ? "bg-green-100 text-green-700"
                          : row.returnRate > 40
                            ? "bg-amber-100 text-amber-700"
                            : "bg-red-100 text-red-700"
                          }`}>
                          {row.returnRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      )}

      {activeTab === "revenue" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-6 border-b border-gray-200 pb-3">
            <div>
              <div className="text-2xl font-semibold">{fmtCurrency(data?.revenueSummary?.arr)}</div>
              <div className="text-xs text-gray-500 uppercase">ARR</div>
            </div>
            <div>
              <div className="text-2xl font-semibold">{fmtCurrency(data?.revenueSummary?.mrr)}</div>
              <div className="text-xs text-gray-500 uppercase">MRR</div>
            </div>
            <div>
              <div className="text-2xl font-semibold">{fmtCurrency(data?.revenueSummary?.arpu)}</div>
              <div className="text-xs text-gray-500 uppercase">ARPU / month</div>
            </div>
            <div>
              <div className="text-2xl font-semibold">{fmtCurrency(data?.revenueSummary?.ltv)}</div>
              <div className="text-xs text-gray-500 uppercase">Est. LTV</div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="MRR trend" subtitle="Monthly recurring revenue over time">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmtCurrency(v)} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Area type="monotone" dataKey="mrr" stroke={COLORS.primary} fill={`${COLORS.primary}33`} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Revenue by plan" subtitle="Individual / group · monthly / annually">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={(data?.revenueByPlan || []).map((p) => ({
                  name: p.name,
                  revenue: p.revenue,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmtCurrency(v)} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Bar dataKey="revenue" radius={[3, 3, 0, 0]}>
                    {(data?.revenueByPlan || []).map((entry) => (
                      <Cell key={entry.key} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <ChartCard title="Revenue by plan & billing">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="pb-2">Plan</th>
                    <th className="pb-2">Billing</th>
                    <th className="pb-2">Revenue</th>
                    <th className="pb-2">Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.revenueByPlan || []).length ? (
                    data.revenueByPlan.map((row) => (
                      <tr key={row.key} className="border-b border-gray-100">
                        <td className="py-2 font-medium capitalize">{row.planKey}</td>
                        <td className="py-2 text-gray-500 capitalize">{row.billingPeriod}</td>
                        <td className="py-2 text-green-600 font-medium">{fmtCurrency(row.revenue)}</td>
                        <td className="py-2 text-gray-500">{fmt(row.transactions)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-gray-500">
                        No completed payments in this range
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </ChartCard>
          <ChartCard title="Plan revenue trend" subtitle="Stacked by plan and billing period">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartRows} margin={TIME_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis {...TIME_X_AXIS} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmtCurrency(v)} />
                <Tooltip formatter={(v) => fmtCurrency(v)} />
                <Bar dataKey="individualMonthly" stackId="plan" fill={COLORS.teal} name="Individual · Monthly" />
                <Bar dataKey="individualAnnually" stackId="plan" fill={COLORS.blue} name="Individual · Annually" />
                <Bar dataKey="groupMonthly" stackId="plan" fill={COLORS.purple} name="Group · Monthly" />
                <Bar dataKey="groupAnnually" stackId="plan" fill={COLORS.amber} name="Group · Annually" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Revenue breakdown">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="pb-2">Period</th>
                    <th className="pb-2">Revenue</th>
                    <th className="pb-2">MRR</th>
                    <th className="pb-2">Transactions</th>
                    <th className="pb-2">Paid users</th>
                    <th className="pb-2">Avg / user</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.revenueBreakdown || []).map((row) => (
                    <tr key={row.period} className="border-b border-gray-100">
                      <td className="py-2">{row.period}</td>
                      <td className="py-2 text-green-600 font-medium">{fmtCurrency(row.revenue)}</td>
                      <td className="py-2 text-indigo-600">{fmtCurrency(row.mrr)}</td>
                      <td className="py-2 text-gray-500">{fmt(row.transactions)}</td>
                      <td className="py-2 text-gray-500">{fmt(row.paidUsers)}</td>
                      <td className="py-2 text-amber-600">{fmtCurrency(row.avgPerUser)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      )}

      {activeTab === "subs" && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="Subscription health" subtitle="Active, at-risk, and churned accounts">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data?.subscriptionHealth || []} dataKey="value" nameKey="name" innerRadius={48} outerRadius={68}>
                    {(data?.subscriptionHealth || []).map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {(data?.subscriptionHealth || []).map((h) => (
                  <div key={h.name} className="flex justify-between text-xs text-gray-600">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm" style={{ background: h.color }} />
                      {h.name}
                    </span>
                    <span className="font-medium text-gray-900">{fmt(h.value)}</span>
                  </div>
                ))}
              </div>
            </ChartCard>
            <ChartCard title="Renewal rate over time" subtitle="Renewals vs churn events">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartRows} margin={TIME_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis {...TIME_X_AXIS} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="renewed" stroke={COLORS.teal} dot={false} name="Renewed" />
                  <Line type="monotone" dataKey="churned" stroke={COLORS.red} dot={false} strokeDasharray="4 4" name="Churned" />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <ChartCard title="Subscriptions expiring soon">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-900">At-risk accounts</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {(data?.expiringSubscriptions || []).length} at risk
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="pb-2">Account</th>
                    <th className="pb-2">Plan</th>
                    <th className="pb-2">Expires in</th>
                    <th className="pb-2">MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.expiringSubscriptions || []).length ? (
                    data.expiringSubscriptions.map((row) => (
                      <tr key={`${row.userId}-${row.days}`} className="border-b border-gray-100">
                        <td className="py-2 font-medium">{row.account}</td>
                        <td className="py-2 text-gray-500">{row.plan}</td>
                        <td className="py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${row.days <= 7 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                            }`}>
                            {row.days}d
                          </span>
                        </td>
                        <td className="py-2 text-green-600 font-medium">{fmtCurrency(row.mrr)}/mo</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-gray-500">
                        No subscriptions expiring in the next 30 days
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      )}

      {activeTab === "feedback" && (
        <SupportFeedbackAdmin chartRows={chartRows} lastUpdated={lastUpdated} />
      )}

      {activeTab === "transactions" && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div />
            <div className="flex items-center gap-2">
              <input
                type="search"
                value={searchVal}
                onChange={(e) => {
                  setSearchVal(e.target.value);
                }}
                placeholder="Search user or ID..."
                className="h-9 w-64 rounded-md border border-gray-200 px-3 text-xs focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            </div>
          </div>

          <div className="space-y-3">
            <ChartCard title="Transactions">
              <div className="flex flex-wrap gap-2 mb-3">
                {["all", "completed", "pending", "failed"].map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setTxFilter(filter)}
                    className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${txFilter === filter
                      ? "border-purple-300 bg-purple-50 text-purple-700 font-medium"
                      : "border-gray-200 text-gray-500 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200"
                      }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                      <th className="pb-2">ID</th>
                      <th className="pb-2">User</th>
                      <th className="pb-2">Plan</th>
                      <th className="pb-2">Action</th>
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length ? (
                      filteredTransactions.map((row) => (
                        <tr key={row.id} className="border-b border-gray-100">
                          <td className="py-2 text-indigo-600 text-xs">{row.id?.slice(0, 8)}…</td>
                          <td className="py-2">{row.user}</td>
                          <td className="py-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                              {row.plan}
                            </span>
                          </td>
                          <td className="py-2 text-gray-500">{row.action}</td>
                          <td className="py-2 text-gray-500 text-xs">
                            {row.date ? new Date(row.date).toLocaleDateString() : "—"}
                          </td>
                          <td className="py-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(row.status)}`}>
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-gray-500">
                          No transactions match filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          </div>
        </div>
      )}
    </div>
  );
}
